import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState
} from 'react'
import { CRASH_ID_KEY } from '../pages/DataMerginAndValidation/utils'
import { distanzaDamerauLevenshtein } from '../pages/DataMerginAndValidation/functions/calculateDistance.js'
import {
  BASIC_ORG,
  BASIC_TO_ROLE,
  createEmptyCrashByRole,
  enrichCrashDetail,
  hasCrashRoleData,
} from '../pages/DataMerginAndValidation/merge-constants'
import { getPersonKey, resolvePeopleToMerge, collectPeopleFromCrash, buildMergeUnitSlots, stripUnitPeople, collectCarriedPeopleFromMatchDetails } from '../pages/DataMerginAndValidation/match-display-rows'
import {
  buildValidationSteps,
  dispatchValidationStep,
  findFirstUndoneStepIndex,
  findStepIndexForTargetStep,
} from '../utils/validation-steps'
import {
  getAnchorPeopleForMergeForms,
  getConfirmedMergePeople,
  hasUnresolvedPairCollisions,
} from '../pages/DataMerginAndValidation/match-pairs'

export const STATE_MACHINE = {
  CRASH_TABLE: 'CRASH_TABLE',
  MATCH_TABLE: 'MATCH_TABLE',
  MERGE_PAGE: 'MERGE_PAGE',
  MERGE_FORM: 'MERGE_FORM',
}

export const MergeContextProv = createContext(undefined)

export const MERGE_CASES = {
  MERGE_ROAD: 'merge_road',
  MERGE_UNIT: 'merge_unit',
  MERGE_PERSON: 'merge_person',
  MERGE_CARRIED_PERSON: 'merge_carried_person',
  SAVE_DATA: 'save_data',
}

const formFactory = (police, hospital, insurance) => ({
  police,
  hospital,
  insurance,
})

const CRASH_KEY = ['crash']
const ROAD_KEY = ['road']

export const UPDATE_CASES = {
  SAVE_CRASH: 'save_crash',
  SAVE_UNIT: 'save_unit',
  SAVE_PERSON: 'save_person',
  SAVE_CARRIED_PERSON: 'save_carried_person',
}

const NAME_KEY = ['Name']
const SURNAME_KEY = ['Surname']

export const MODES = {
  MERGING: 'merging',
  VALIDATING: 'validating',
}

export const MergeContext = ({ children }) => {
  const [crashes, setCrashes] = useState([])
  const [mergedCrashes, setMergedCrashes] = useState([])
  const [currentState, setCurrentState] = useState(STATE_MACHINE.CRASH_TABLE)
  const [selectedCrash, setSelectedCrash] = useState(false)
  const [matches, setMatches] = useState([])
  const [sourceBasicOrganization, setSourceBasicOrganization] = useState(BASIC_ORG.POLICE)
  /** Rolling window for merge list fetches (GET /crash/all?days=). Default 7. */
  const [daysFilter, setDaysFilter] = useState(7)
  const [crashByRole, setCrashByRole] = useState(createEmptyCrashByRole)
  const [anchorCrashId, setAnchorCrashId] = useState(null)
  const [mode, setMode] = useState(MODES.VALIDATING)
  const [policePhotos, setPolicePhotos] = useState([])
  const [collisionChoicesByRole, setCollisionChoicesByRole] = useState({})
  const [validationStepIndex, setValidationStepIndex] = useState(null)

  const policeDetail = crashByRole.police
  const hospitalDetail = crashByRole.hospital
  const insuranceDetail = crashByRole.insurance

  const anchorRole = BASIC_TO_ROLE[sourceBasicOrganization] ?? 'police'
  const anchorDetail = crashByRole[anchorRole] ?? null

  const selectedCrashDetail = anchorDetail ?? {}
  const selectedMatchDetailHospital = hospitalDetail ?? {}
  const selectedMatchDetailInsurance = insuranceDetail ?? {}

  const setCrashForRole = useCallback((role, detail) => {
    setCrashByRole((prev) => ({ ...prev, [role]: detail }))
  }, [])

  const setSelectedCrashDetail = useCallback(
    (detail) => setCrashForRole('police', detail),
    [setCrashForRole]
  )

  const setSelectedMatchDetailHospital = useCallback(
    (detail) => setCrashForRole('hospital', detail),
    [setCrashForRole]
  )

  const setSelectedMatchDetailInsurance = useCallback(
    (detail) => setCrashForRole('insurance', detail),
    [setCrashForRole]
  )

  const resetMatchSelections = useCallback(() => {
    setCrashByRole(createEmptyCrashByRole())
    setAnchorCrashId(null)
    setSelectedCrash(false)
    setMatches([])
    setPolicePhotos([])
    setCollisionChoicesByRole({})
  }, [])

  const setCollisionChoice = useCallback((role, matchPersonKey, anchorPersonKey) => {
    setCollisionChoicesByRole((prev) => ({
      ...prev,
      [role]: {
        ...(prev[role] ?? {}),
        [matchPersonKey]: anchorPersonKey,
      },
    }))
  }, [])

  const clearCollisionChoicesForRole = useCallback((role) => {
    setCollisionChoicesByRole((prev) => {
      if (!prev[role]) return prev
      const next = { ...prev }
      delete next[role]
      return next
    })
  }, [])

  const changeSourceBasicOrganization = useCallback((basicOrg) => {
    setSourceBasicOrganization(basicOrg)
    resetMatchSelections()
    setCurrentState(STATE_MACHINE.CRASH_TABLE)
  }, [resetMatchSelections])

  const mergeCapabilities = useMemo(() => {
    const hasPolice = hasCrashRoleData(policeDetail)
    const hasHospital = hasCrashRoleData(hospitalDetail)
    const hasInsurance = hasCrashRoleData(insuranceDetail)
    const selectedRoleCount = [hasPolice, hasHospital, hasInsurance].filter(Boolean).length
    /** Merge tra almeno due fonti (non richiede tutte e tre). */
    const canMerge = selectedRoleCount >= 2
    const canValidateOnly =
      sourceBasicOrganization === BASIC_ORG.POLICE &&
      hasPolice &&
      selectedRoleCount === 1
    return {
      canMerge,
      canValidateOnly,
      hasPolice,
      hasHospital,
      hasInsurance,
      selectedRoleCount,
    }
  }, [policeDetail, hospitalDetail, insuranceDetail, sourceBasicOrganization])

  useEffect(() => {
    const hasMatches =
      hasCrashRoleData(hospitalDetail) || hasCrashRoleData(insuranceDetail)
    if (hasMatches) {
      setMode(MODES.MERGING)
    } else if (
      hasCrashRoleData(policeDetail) &&
      sourceBasicOrganization === BASIC_ORG.POLICE
    ) {
      setMode(MODES.VALIDATING)
    }
  }, [hospitalDetail, insuranceDetail, policeDetail, sourceBasicOrganization])

  const confirmedMergePeople = useMemo(
    () => getConfirmedMergePeople(
      anchorDetail,
      crashByRole,
      anchorRole,
      collisionChoicesByRole,
    ),
    [anchorDetail, crashByRole, anchorRole, collisionChoicesByRole],
  )

  const peopleToMergeFromPairs = useMemo(
    () => getAnchorPeopleForMergeForms(confirmedMergePeople),
    [confirmedMergePeople],
  )

  const hasPairBasedMerge = peopleToMergeFromPairs.length > 0

  const peopleToMerge = useMemo(() => {
    if (hasPairBasedMerge && peopleToMergeFromPairs.length > 0) {
      return peopleToMergeFromPairs
    }
    return resolvePeopleToMerge(
      anchorDetail?.people,
      hospitalDetail,
      insuranceDetail,
    )
  }, [
    hasPairBasedMerge,
    peopleToMergeFromPairs,
    anchorDetail?.people,
    hospitalDetail,
    insuranceDetail,
  ])

  const carriedPeople = useMemo(
    () => collectCarriedPeopleFromMatchDetails([
      { detail: policeDetail, role: 'police', roleLabel: 'Police' },
      { detail: hospitalDetail, role: 'hospital', roleLabel: 'Hospital' },
      { detail: insuranceDetail, role: 'insurance', roleLabel: 'Insurance' },
    ]),
    [policeDetail, hospitalDetail, insuranceDetail],
  )

  const unitSlots = useMemo(
    () => buildMergeUnitSlots(anchorDetail, crashByRole),
    [anchorDetail, crashByRole],
  )

  const validationSteps = useMemo(
    () => buildValidationSteps({
      unitSlots,
      peopleToMerge,
      carriedPeople,
    }),
    [unitSlots, peopleToMerge, carriedPeople],
  )

  const hasValidationWizard = validationSteps.length > 1

  const hasUnresolvedCollisions = useMemo(
    () => hasUnresolvedPairCollisions(
      anchorDetail,
      crashByRole,
      anchorRole,
      collisionChoicesByRole,
    ),
    [anchorDetail, crashByRole, anchorRole, collisionChoicesByRole],
  )

  const findFuzzyMatchPerson = (detail, person) => {
    if (!detail || !person) return {}
    return collectPeopleFromCrash(detail).find(
      (p) =>
        distanzaDamerauLevenshtein(person?.[NAME_KEY], p?.[NAME_KEY]) < 4 &&
        distanzaDamerauLevenshtein(person?.[SURNAME_KEY], p?.[SURNAME_KEY]) < 4,
    ) ?? {}
  }

  function reducer (state, action) {
    switch (action.type) {
      case MERGE_CASES.MERGE_ROAD: {
        setCurrentState(STATE_MACHINE.MERGE_FORM)
        return {
          ...formFactory(
            {
              [CRASH_KEY]: anchorDetail?.[CRASH_KEY],
              [ROAD_KEY]: anchorDetail?.[ROAD_KEY],
            },
            {
              [CRASH_KEY]: hospitalDetail?.[CRASH_KEY],
              [ROAD_KEY]: hospitalDetail?.[ROAD_KEY],
            },
            {
              [CRASH_KEY]: insuranceDetail?.[CRASH_KEY],
              [ROAD_KEY]: insuranceDetail?.[ROAD_KEY],
            }
          ),
          type: UPDATE_CASES.SAVE_CRASH,
        }
      }
      case MERGE_CASES.MERGE_UNIT: {
        setCurrentState(STATE_MACHINE.MERGE_FORM)
        const unitIndex = action.index
        return {
          ...formFactory(
            { unit: stripUnitPeople(policeDetail?.units?.[unitIndex]) },
            { unit: stripUnitPeople(hospitalDetail?.units?.[unitIndex]) },
            { unit: stripUnitPeople(insuranceDetail?.units?.[unitIndex]) },
          ),
          type: UPDATE_CASES.SAVE_UNIT,
          index: unitIndex,
        }
      }
      case MERGE_CASES.MERGE_PERSON: {
        setCurrentState(STATE_MACHINE.MERGE_FORM)
        const mergeEntries = hasPairBasedMerge
          ? confirmedMergePeople
          : resolvePeopleToMerge(
              anchorDetail?.people,
              hospitalDetail,
              insuranceDetail,
            ).map((person) => ({ anchorPerson: person, matches: {} }))

        const entry = mergeEntries[action.index]
        const myPerson = entry?.anchorPerson
        const originalIndex = (anchorDetail?.people ?? []).findIndex(
          (person) => getPersonKey(person) === getPersonKey(myPerson),
        )

        const policePerson = anchorRole === 'police'
          ? myPerson
          : (entry?.matches?.police ?? findFuzzyMatchPerson(policeDetail, myPerson))
        const hospitalPerson = anchorRole === 'hospital'
          ? myPerson
          : (entry?.matches?.hospital ?? findFuzzyMatchPerson(hospitalDetail, myPerson))
        const insurancePerson = anchorRole === 'insurance'
          ? myPerson
          : (entry?.matches?.insurance ?? findFuzzyMatchPerson(insuranceDetail, myPerson))

        return {
          ...formFactory(
            { person: { ...policePerson } },
            { person: { ...hospitalPerson } },
            { person: { ...insurancePerson } },
          ),
          type: UPDATE_CASES.SAVE_PERSON,
          index: originalIndex,
          personKey: getPersonKey(myPerson),
        }
      }
      case MERGE_CASES.MERGE_CARRIED_PERSON: {
        setCurrentState(STATE_MACHINE.MERGE_FORM)
        const carryRole = action.role
        const carriedPerson = action.person ?? {}
        const sourceDetail = carryRole ? crashByRole?.[carryRole] : null
        const sourcePerson =
          sourceDetail?.people?.find(
            (person) => getPersonKey(person) === getPersonKey(carriedPerson),
          ) ?? carriedPerson

        return {
          ...formFactory(
            carryRole === 'police' ? { person: { ...sourcePerson } } : {},
            carryRole === 'hospital' ? { person: { ...sourcePerson } } : {},
            carryRole === 'insurance' ? { person: { ...sourcePerson } } : {},
          ),
          type: UPDATE_CASES.SAVE_CARRIED_PERSON,
          personKey: getPersonKey(carriedPerson),
          role: carryRole,
        }
      }

      default: {
        throw Error('Unknown action: ' + action.type)
      }
    }
  }

  const [mergeState, dispatchMergeState] = useReducer(reducer)

  const enterValidationStep = useCallback((index) => {
    const step = validationSteps[index]
    if (!step) return
    setValidationStepIndex(index)
    dispatchValidationStep(step, dispatchMergeState)
  }, [validationSteps, dispatchMergeState])

  const startValidationWizard = useCallback(() => {
    const firstUndone = findFirstUndoneStepIndex(validationSteps, {
      crashDone: Boolean(anchorDetail?.done),
      unitSlots,
      peopleToMerge,
      carriedPeople,
    })
    enterValidationStep(firstUndone >= 0 ? firstUndone : 0)
  }, [
    validationSteps,
    anchorDetail?.done,
    unitSlots,
    peopleToMerge,
    carriedPeople,
    enterValidationStep,
  ])

  const goToPreviousValidationStep = useCallback(() => {
    if (validationStepIndex === null || validationStepIndex <= 0) return
    enterValidationStep(validationStepIndex - 1)
  }, [validationStepIndex, enterValidationStep])

  const goToNextValidationStep = useCallback(() => {
    if (validationStepIndex === null) return
    if (validationStepIndex + 1 >= validationSteps.length) return
    enterValidationStep(validationStepIndex + 1)
  }, [validationStepIndex, validationSteps.length, enterValidationStep])

  const goToMergeOverview = useCallback(() => {
    setValidationStepIndex(null)
    setCurrentState(STATE_MACHINE.MERGE_PAGE)
  }, [])

  const openValidationStepForTarget = useCallback((targetStep) => {
    const index = findStepIndexForTargetStep(validationSteps, targetStep)
    if (index >= 0) {
      enterValidationStep(index)
    }
  }, [validationSteps, enterValidationStep])

  const finishValidationWizardOrReturnToOverview = useCallback(() => {
    if (
      validationStepIndex !== null &&
      validationStepIndex + 1 < validationSteps.length
    ) {
      enterValidationStep(validationStepIndex + 1)
      return
    }
    setValidationStepIndex(null)
    setCurrentState(STATE_MACHINE.MERGE_PAGE)
  }, [validationStepIndex, validationSteps.length, enterValidationStep])

  const handleBack = (currentStateValue) => {
    if (currentStateValue === STATE_MACHINE.MERGE_FORM && validationStepIndex !== null) {
      if (validationStepIndex > 0) {
        enterValidationStep(validationStepIndex - 1)
        return
      }
      goToMergeOverview()
      return
    }

    const chronoOrder = Object.values(STATE_MACHINE)?.map((v) => v)
    const actualStateKey = chronoOrder?.findIndex((item) => item === currentStateValue)
    const INDEX_TO_JUMP_BACKWARD = 1
    const prevKey = actualStateKey - INDEX_TO_JUMP_BACKWARD
    setValidationStepIndex(null)
    setCurrentState(chronoOrder?.[prevKey])
  }

  const handleSelectedCrash = (id, merged = false, images = []) => {
    setSelectedCrash(id)
    setAnchorCrashId(id || null)
    if (id) {
      setCurrentState(STATE_MACHINE.MATCH_TABLE)
      const myCrash = (merged ? mergedCrashes : crashes)?.find(
        (c) => c?.crash?.[CRASH_ID_KEY] === id
      )
      const anchorRole = BASIC_TO_ROLE[sourceBasicOrganization] ?? 'police'
      const enriched = enrichCrashDetail(myCrash)
      const nextByRole = createEmptyCrashByRole()
      nextByRole[anchorRole] = enriched
      setCrashByRole(nextByRole)
      if (anchorRole === 'police' && images?.length) {
        setPolicePhotos(images)
      }
    }
  }

  const updateSelectedCrashDetail = (action) => {
    switch (action.type) {
      case UPDATE_CASES.SAVE_CRASH: {
        setCrashForRole(anchorRole, { ...anchorDetail, ...action.data })
        finishValidationWizardOrReturnToOverview()
        break
      }
      case UPDATE_CASES.SAVE_UNIT: {
        const unitSlots = buildMergeUnitSlots(anchorDetail, crashByRole)
        const nextUnits = unitSlots.map((uni, index) => {
          if (index === action.index) {
            return { ...action.data?.unit, done: true }
          }
          return uni
        })
        setCrashForRole(anchorRole, {
          ...anchorDetail,
          units: nextUnits,
        })
        finishValidationWizardOrReturnToOverview()
        break
      }
      case UPDATE_CASES.SAVE_PERSON: {
        const personKey = action.personKey
        setCrashForRole(anchorRole, {
          ...anchorDetail,
          people: anchorDetail?.people?.map((p, index) => {
            const isTarget = personKey
              ? getPersonKey(p) === personKey
              : index === action.index
            if (isTarget) {
              return {
                ...p,
                ...action.data?.person,
                unitIndex: p.unitIndex,
                personIndex: p.personIndex,
                done: true,
              }
            }
            return p
          }),
        })
        finishValidationWizardOrReturnToOverview()
        break
      }
      case UPDATE_CASES.SAVE_CARRIED_PERSON: {
        const carryRole = action.role
        if (!carryRole || !crashByRole?.[carryRole]) {
          throw new Error('Missing role for carried person save')
        }
        const roleDetail = crashByRole[carryRole]
        setCrashForRole(carryRole, {
          ...roleDetail,
          people: (roleDetail.people ?? []).map((person) => {
            const isTarget = action.personKey
              ? getPersonKey(person) === action.personKey
              : false
            if (!isTarget) return person
            return {
              ...person,
              ...action.data?.person,
              unitIndex: person.unitIndex,
              personIndex: person.personIndex,
              done: true,
              includeInMerge: true,
              isMatchCandidate: false,
            }
          }),
        })
        finishValidationWizardOrReturnToOverview()
        break
      }
      default: {
        throw new Error('Not found action')
      }
    }
  }

  return (
    <MergeContextProv.Provider
      value={{
        crashes: crashes || [],
        setCrashes,
        currentState,
        setCurrentState,
        handleSelectedCrash,
        selectedCrash,
        anchorCrashId,
        matches,
        setMatches,
        sourceBasicOrganization,
        setSourceBasicOrganization: changeSourceBasicOrganization,
        daysFilter,
        setDaysFilter,
        crashByRole,
        setCrashByRole,
        setCrashForRole,
        setSelectedCrashDetail,
        setSelectedMatchDetailHospital,
        setSelectedMatchDetailInsurance,
        selectedCrashDetail,
        selectedMatchDetailHospital,
        selectedMatchDetailInsurance,
        policeDetail,
        hospitalDetail,
        insuranceDetail,
        anchorRole,
        anchorDetail,
        mergeCapabilities,
        confirmedMergePeople,
        peopleToMergeFromPairs,
        hasPairBasedMerge,
        hasUnresolvedCollisions,
        collisionChoicesByRole,
        setCollisionChoice,
        clearCollisionChoicesForRole,
        mergeState,
        dispatchMergeState,
        updateSelectedCrashDetail,
        mergedCrashes,
        setMergedCrashes,
        handleBack,
        mode,
        policePhotos,
        setPolicePhotos,
        resetMatchSelections,
        peopleToMerge,
        carriedPeople,
        unitSlots,
        validationSteps,
        validationStepIndex,
        hasValidationWizard,
        startValidationWizard,
        enterValidationStep,
        goToPreviousValidationStep,
        goToNextValidationStep,
        goToMergeOverview,
        openValidationStepForTarget,
      }}
    >
      {children}
    </MergeContextProv.Provider>
  )
}

export function useMergeContext () {
  const context = useContext(MergeContextProv)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
